import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { SERVER_URL } from "../services/Http";
import documentoDocenteService from "../services/documentoDocenteService";
import ComparativaDocentePanel from "./ComparativaDocentePanel";
import VisorPdf, { puedeMostrarPdfEnIframe } from "./VisorPdf";

// En Android e iOS el <iframe> no muestra el PDF: ahí se usa el visor de pdf.js
const usarIframe = puedeMostrarPdfEnIframe();

const SesionEnModal = ({ modalOpen, handleCloseModal, sesionSeleccionada, onPdfNotFound, }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);

  // Sesión generada a partir de un documento del docente (comparativa)
  const [conDocumento, setConDocumento] = useState(false);
  const [documentoRevisado, setDocumentoRevisado] = useState(false);
  const [tab, setTab] = useState("sesion");
  const esMovil = useMediaQuery((theme) => theme.breakpoints.down("md"));

  /* =========================
     Cargar PDF
  ========================= */
  useEffect(() => {
    if (!modalOpen || !sesionSeleccionada) return;
    let nextBlobUrl = null;
    let isCancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(false);
      setBlobUrl(null);
      setPdfBlob(null);

      try {
        const res = await fetch(
          `${SERVER_URL}/descargar/ver-pdf/${sesionSeleccionada.id}`
        );

        const type = res.headers.get("content-type") || "";

        if (!res.ok || !type.includes("pdf")) {
          throw new Error("NO_PDF");
        }

        const blob = await res.blob();
        nextBlobUrl = URL.createObjectURL(blob);
        if (!isCancelled) {
          setBlobUrl(nextBlobUrl);
          setPdfBlob(blob);
        }
      } catch {
        if (!isCancelled) {
          setError(true);
        }
        if (!isCancelled && onPdfNotFound && sesionSeleccionada?.id) {
          onPdfNotFound(sesionSeleccionada.id);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (nextBlobUrl) {
        URL.revokeObjectURL(nextBlobUrl);
      }
    };
  }, [modalOpen, sesionSeleccionada, onPdfNotFound]);

  /* =========================
     ¿Tiene documento del docente?
  ========================= */
  useEffect(() => {
    if (!modalOpen || !sesionSeleccionada) return;
    let isCancelled = false;

    setTab("sesion");
    setConDocumento(false);
    setDocumentoRevisado(false);

    documentoDocenteService
      .obtenerDocumentoDeSesion(sesionSeleccionada.id)
      .then((data) => {
        if (!isCancelled) setConDocumento(Boolean(data));
      })
      .catch((err) => {
        console.error("Error obteniendo el documento del docente:", err);
      })
      .finally(() => {
        if (!isCancelled) setDocumentoRevisado(true);
      });

    return () => {
      isCancelled = true;
    };
  }, [modalOpen, sesionSeleccionada]);

  /* =========================
     ⏱ Autocierre si hay error
     (no se cierra si queda la comparativa por ver)
  ========================= */
  useEffect(() => {
    if (!error || !documentoRevisado || conDocumento) return;

    const timer = setTimeout(() => {
      handleCloseModal();
    }, 3000); // 3 segundos

    return () => clearTimeout(timer);
  }, [error, documentoRevisado, conDocumento, handleCloseModal]);

  if (!modalOpen || !sesionSeleccionada) return null;

  return (
    <Modal open={modalOpen} onClose={handleCloseModal}>
      <Box
        sx={{
          // En celulares ocupa toda la pantalla
          position: { xs: "absolute", md: "static" },
          inset: { xs: 0, md: "auto" },
          width: { xs: "100%", md: "92vw" },
          height: { xs: "100%", md: "92vh" },
          bgcolor: "#fff",
          m: { xs: 0, md: "4vh auto" },
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 0, md: 3 },
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(2, 6, 23, 0.45)",
          outline: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 1.5, md: 2.5 },
            py: 1.25,
            bgcolor: "#031A7F",
            color: "#fff",
          }}
        >
          <PictureAsPdfOutlinedIcon sx={{ color: "#00C7DB" }} />
          <Typography fontWeight={700} noWrap sx={{ flex: 1 }}>
            {sesionSeleccionada.tema}
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {conDocumento && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              px: { xs: 0, md: 1.5 },
              minHeight: 46,
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              "& .MuiTab-root": { minHeight: 46, textTransform: "none", fontWeight: 700, color: "#475569" },
              "& .Mui-selected": { color: "#031A7F !important" },
              "& .MuiTabs-indicator": { height: 3, borderRadius: 3, bgcolor: "#00C7DB" },
            }}
          >
            <Tab value="sesion" icon={<PictureAsPdfOutlinedIcon fontSize="small" />} iconPosition="start" label="Sesión generada" />
            <Tab
              value="comparativa"
              icon={<CompareArrowsIcon fontSize="small" />}
              iconPosition="start"
              label={esMovil ? "Comparativa" : "Comparativa con el documento del docente"}
            />
          </Tabs>
        )}

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {tab === "sesion" && (
            <>
              {loading && <CircularProgress sx={{ m: 2 }} />}

              {error && (
                <Alert severity="warning" sx={{ m: 2 }}>
                  No se encontró el documento de la sesión.
                </Alert>
              )}

              {!loading && blobUrl && usarIframe && (
                <iframe
                  src={blobUrl}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title="PDF"
                />
              )}

              {!loading && pdfBlob && !usarIframe && (
                <Box sx={{ height: "100%", p: 1, bgcolor: "#f8fafc" }}>
                  <VisorPdf pdfBlob={pdfBlob} nombre={sesionSeleccionada.tema} />
                </Box>
              )}
            </>
          )}

          {tab === "comparativa" && conDocumento && (
            <ComparativaDocentePanel
              sesionId={sesionSeleccionada.id}
              nombreSesion={sesionSeleccionada.tema}
              mostrarNombre={false}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default SesionEnModal;

